import type { PageContentDimensions } from '../hooks/usePageContentDimensions';

type HideWhiteSpaceParams = {
	shouldRenderFixedStyle: boolean;
	pageContentDimensions: PageContentDimensions;
	fallbackWidth?: string;
};

type HideWhiteSpaceReturn = {
	width?: string;
	left?: string;
	right?: string;
};

/**
 * Utility function to conditionally apply page content positioning based on shouldRenderFixedStyle
 * @param shouldRenderFixedStyle - Whether to apply fixed styling
 * @param pageContentDimensions - Page content dimensions from usePageContentDimensions hook
 * @param fallbackWidth - Fallback width when not using fixed style (defaults to '100%')
 * @returns Style object with conditional positioning
 */
export const hideWhiteSpace = ({
	shouldRenderFixedStyle,
	pageContentDimensions,
	fallbackWidth = '100%',
}: HideWhiteSpaceParams): HideWhiteSpaceReturn => {
	if (!shouldRenderFixedStyle) {
		return {
			width: fallbackWidth,
		};
	}

	const {
		width: pageContentWidth,
		left: pageContentLeft,
		right: pageContentRight,
	} = pageContentDimensions;

	// Handle case where dimensions might be undefined or width might be a string
	const width = typeof pageContentWidth === 'number' ? pageContentWidth : 0;
	const left = pageContentLeft ?? 0;
	const right = pageContentRight ?? 0;

	return {
		left: left > 0 ? `${left}px` : '0px',
		right: right > 0 ? `${window.innerWidth - right}px` : '0px',
		width: width > 0 ? `${width}px` : '100%',
	};
};
